<template>
    <div v-if="showProjectViewLayoutRight" class="ProjectViewLayoutRight">
        <CHeading :variant="3"> Preferencie </CHeading>

        <CCardBox>
            <CFormGroup v-if="project.intensityValue && project.intensityUnit" label="Intenzita">
                {{ project.intensityValue }}
                {{ intensityUnits }}
            </CFormGroup>

            <CFormGroup v-if="hasWanted" label="Hľadáme">
                <ul>
                    <li v-for="item in project.wanted" :key="item">
                        {{ projectWantedItem(item) }}
                    </li>
                </ul>
            </CFormGroup>

            <CFormGroup v-if="hasAudience" label="Kto sa môže zapojiť">
                <CList>
                    <li v-for="item in audienceLabels" :key="item">
                        {{ item }}
                    </li>
                </CList>
            </CFormGroup>

            <CFormGroup v-if="isVolunteeringWeekProject" label="Kedy">
                <div>{{ volunteeringWeekDateLabel }}</div>

                <div v-if="volunteeringWeekTimeRangeLabel" class="pt-0.5 text-lg leading-5 text-black dark:text-txt-headings">
                    <strong>{{ volunteeringWeekTimeRangeLabel }}</strong>
                </div>
            </CFormGroup>

            <CFormGroup v-else-if="hasDuration" label="Dĺžka projektu">
                <CList>
                    <li v-for="item in project.duration" :key="item">
                        {{ projectDurationItem(item) }}
                    </li>
                </CList>
            </CFormGroup>

            <CFormGroup v-if="hasDisabilities" label="Vhodné aj pre osoby">
                <CList>
                    <li v-for="item in project.disabilities" :key="item">
                        {{ projectDisabilityItem(item) }}
                    </li>
                </CList>
            </CFormGroup>
        </CCardBox>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import groupBy from 'lodash-es/groupBy'
import mapValues from 'lodash-es/mapValues'

import { PROJECT_DISABILITY_OPTIONS, valueLookup } from '#shared/types/dictionary'
import { wanted } from '#shared/types/education'
import { MentoringCategoriesMapped, MentoringCategory } from '#shared/types/Mentoring'
import { ProjectType, type Project, intensityUnitsMap, projectAudienceLabelsFromProject } from '#shared/types/Project'
import {
    getVolunteeringWeekOpportunityFullDateLabel,
    getVolunteeringWeekOpportunityTimeRangeLabel
} from '@/utils/volunteeringWeekOpportunity'

interface Props {
    project: Project
}

const props = defineProps<Props>()

const isVolunteeringWeekProject = computed(() => props.project.type === ProjectType.VOLUNTEERING_WEEK)

const volunteeringWeekDateLabel = computed(() => getVolunteeringWeekOpportunityFullDateLabel(props.project))

const volunteeringWeekTimeRangeLabel = computed(() => getVolunteeringWeekOpportunityTimeRangeLabel(props.project))

const showProjectViewLayoutRight = computed((): boolean => {
    return Boolean(
        (props.project.intensityValue && props.project.intensityUnit) ||
        hasWanted.value ||
        hasAudience.value ||
        (isVolunteeringWeekProject.value || hasDuration.value) ||
        hasDisabilities.value
    )
})

const intensityUnits = computed((): string => {
    const intensity = props.project.intensityUnit ? intensityUnitsMap[props.project.intensityUnit]?.[0] : undefined
    return intensity?.label || ''
})

const hasDuration = computed((): boolean => {
    return (props.project.duration?.length ?? 0) > 0
})

const hasWanted = computed((): boolean => {
    return (props.project.wanted?.length ?? 0) > 0
})

const audienceLabels = computed(() => {
    return projectAudienceLabelsFromProject({
        who: props.project.who,
        whoGroupEnabled: props.project.whoGroupEnabled
    })
})

const hasAudience = computed((): boolean => {
    return audienceLabels.value.length > 0
})

const hasDisabilities = computed((): boolean => {
    return (props.project.disabilities ?? []).length > 0
})

// Helper methods
function mapValuesStr(optionsList: any[]) {
    return mapValues(groupBy(optionsList, 'id'), (x) => x[0])
}

function valueLookupById(item: string, list: any[]) {
    if (list && item) {
        const option = mapValuesStr(list)
        if (option && option[item]) {
            const result = option[item]
            if ('label' in result) {
                return result.label
            }
        }
    }
    return ''
}

function projectWantedItem(item: string): string {
    return valueLookup(item, wanted) || ''
}

function projectDurationItem(item: string) {
    return valueLookupById(item, MentoringCategoriesMapped[MentoringCategory.DurationProject] ?? [])
}

function projectDisabilityItem(item: string) {
    return valueLookupById(item, PROJECT_DISABILITY_OPTIONS)
}
</script>
